import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatPrice } from "../lib/api";
import { Sparkles, LogOut } from "lucide-react";

export default function Account() {
  const { user, loading, logout, refreshMe } = useAuth();
  const [orders, setOrders] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/"); return; }
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
    refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  if (!user) return null;

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-16">
      <div className="grid grid-cols-12 gap-10">
        <aside className="col-span-12 lg:col-span-4">
          <div className="card-elev rounded-xl p-7">
            <div className="text-[11px] tracking-[0.25em] uppercase text-[#B026FF] font-bold">Account</div>
            <h1 className="mt-2 font-display font-extrabold text-3xl text-white tracking-tighter">{user.name}</h1>
            <div className="text-sm text-white/50 mt-1">{user.email}</div>

            <div className="mt-6 rounded-lg border border-[#B026FF]/30 bg-gradient-to-br from-[#B026FF]/15 to-transparent p-5">
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#B026FF] font-bold flex items-center gap-2">
                <Sparkles size={12} /> Kavo Points
              </div>
              <div className="mt-3 font-mono text-5xl text-white tracking-tight" data-testid="account-points">
                {user.loyalty_points}
              </div>
              <div className="text-xs text-white/50 mt-2">
                Worth <span className="text-white font-mono">{formatPrice(user.loyalty_points * 5)}</span> in store credit
              </div>
            </div>

            <button
              onClick={logout}
              data-testid="account-logout-btn"
              className="mt-6 w-full btn-ghost-violet h-11 rounded-md inline-flex items-center justify-center gap-2"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </aside>

        <section className="col-span-12 lg:col-span-8">
          <h2 className="font-display font-extrabold text-3xl tracking-tighter text-white">Order history</h2>

          {orders.length === 0 ? (
            <div className="mt-6 card-elev rounded-xl p-10 text-center text-white/60">
              No orders yet. Your future pulls will appear here.
            </div>
          ) : (
            <ul className="mt-6 space-y-4">
              {orders.map((o) => (
                <li key={o.id} className="card-elev rounded-xl p-6" data-testid={`order-row-${o.order_number}`}>
                  <div className="flex flex-wrap gap-4 items-center justify-between">
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 font-mono">{o.order_number}</div>
                      <div className="text-sm text-white mt-1">{new Date(o.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">Items</div>
                        <div className="text-sm text-white">{o.items.length}</div>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">Total</div>
                        <div className="font-mono text-white">{formatPrice(o.total_cents)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">Status</div>
                        <div className="text-[#B026FF] text-sm font-medium uppercase">{o.status}</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-xs text-white/40 truncate">
                    {o.items.map((i) => `${i.name} × ${i.quantity}`).join(" · ")}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
